import { PaypointQRCode } from "Components/Paypoints/PaypointQRCode";
import type { PaypointFormData } from "Components/Paypoints/schema";
import { FormSection } from "Components/Shared/FormSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { Resources, useResource } from "Translations/Resources";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import { type Control, useFormState } from "react-hook-form";

const RESOURCES = Resources.Paypoint.Form;

type Props = {
  control: Control<PaypointFormData>;
  // Present once the paypoint exists; drives the QR code.
  paypointPublicID?: string | null;
  paypointName?: string | null;
};

export const PaypointNfcSection = ({
  control,
  paypointPublicID,
  paypointName,
}: Props) => {
  const { t } = useResource();
  const { errors } = useFormState({ control, name: "nfcCardNo" });

  const nfcCardNoHelperText = errors.nfcCardNo?.message || (
    <Typography
      component="span"
      display="flex"
      alignItems="flex-start"
      mt={2}
      gap={1}
    >
      <InfoOutlinedIcon sx={{ height: "1rem", width: "1rem", mt: 0.5 }} />
      {t(RESOURCES.NfcCardNoHelperText)}
    </Typography>
  );

  return (
    <FormSection legend={t(RESOURCES.Legend.NFC)}>
      <Box
        display="flex"
        columnGap={4}
        rowGap={0}
        flexWrap={["wrap", "nowrap"]}
        justifyContent="center"
      >
        {paypointPublicID && (
          <PaypointQRCode
            size={150}
            isDownloadEnabled
            paypointPublicID={paypointPublicID}
            downloadFileName={`paypoint-${paypointName?.toLowerCase().replace(/\s+/g, "-") || ""}-qr-code`}
          />
        )}
        <Box flexGrow={1} minWidth={0}>
          <FormInput
            control={control}
            unmask={false}
            name="nfcCardNo"
            label={t(RESOURCES.NfcCardNo)}
            mask="**-**-**-**-**-**-**" // This makes the form always show as dirty
            placeholder="xx-xx-xx-xx-xx-xx-xx"
            maskPlaceholder="x"
            valueTransformer={(v) => v?.toUpperCase()}
            helperText={nfcCardNoHelperText}
          />
        </Box>
      </Box>
    </FormSection>
  );
};
